/**
 * 图表配色与轴样式 —— **色值一个都不写在这里，全从主题 token 读**。
 *
 * 图表里硬编码一个 `#5470c6`，主题一换就是一块和整个后台格格不入的色 —— 而且没人看得出它是从哪来的。
 *
 * 看板上三个图问的都是**量的大小**（这天/这个模型/这个人烧了多少），所以数据条一律**单色**：
 * 身份由轴上的标签给，不由颜色给。颜色在这里只承担一件事 —— **标状态**：一段时间里有「成本待核算」
 * 的调用，那根柱子转成 warning 色，并且在图注和 tooltip 里说出来（不是只靠颜色）。
 *
 * `readToken` 读不到就抛错：图表画出一片黑而没人知道为什么，比直接红一下难查得多。
 */

/**
 * 当前主题下的图表用色。
 *
 * `themeName` 这个入参**不参与算色**（色值一律从 DOM 上现读，`data-theme` 一换就是新值）——它在这里
 * 只做一件事：把「当前是哪套主题」变成调用方 `computed` 的一个依赖。ECharts 的 option 是一次算出来的
 * **JS 对象**，不认 CSS 变量；不把主题读进 computed，切了主题图表就还是旧色，一直到下次数据变。
 *
 * @param {string} themeName `useAdminTheme()` 的 `theme.value`
 * @returns {{ bar: string, info: string, success: string, warning: string, danger: string, text: string, textMuted: string, border: string, surface: string }}
 */
export function chartTheme(themeName) {
  if (!themeName) throw new Error('chart-theme: 要把 useAdminTheme() 的 theme.value 传进来，否则切换主题时图表不会重画')
  const style = window.getComputedStyle(document.documentElement)
  return {
    bar: readToken(style, '--drama-color-primary'),
    info: readToken(style, '--drama-color-info'),
    success: readToken(style, '--drama-color-success'),
    warning: readToken(style, '--drama-color-warning'),
    danger: readToken(style, '--drama-color-danger'),
    text: readToken(style, '--drama-color-text'),
    textMuted: readToken(style, '--drama-color-text-muted'),
    border: readToken(style, '--drama-color-border'),
    surface: readToken(style, '--drama-color-surface'),
  }
}

/**
 * 三个图共用的底座：tooltip 外观、字色。轴和网格退到底噪，数据条才是主角。
 * @param {ReturnType<typeof chartTheme>} theme
 */
export function chartBase(theme) {
  return {
    textStyle: { color: theme.textMuted, fontSize: 12 },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: theme.surface,
      borderColor: theme.border,
      borderWidth: 1,
      textStyle: { color: theme.text, fontSize: 12 },
    },
  }
}

/** @param {ReturnType<typeof chartTheme>} theme */
export function categoryAxis(theme) {
  return {
    type: 'category',
    axisLine: { lineStyle: { color: theme.border } },
    axisTick: { show: false },
    axisLabel: { color: theme.textMuted },
  }
}

/** @param {ReturnType<typeof chartTheme>} theme */
export function valueAxis(theme) {
  return {
    type: 'value',
    splitLine: { lineStyle: { color: theme.border, type: 'dashed' } },
    axisLabel: { color: theme.textMuted },
  }
}

/**
 * @param {CSSStyleDeclaration} style
 * @param {string} name
 */
function readToken(style, name) {
  const value = style.getPropertyValue(name).trim()
  if (!value) throw new Error(`chart-theme: 主题里没有 token ${name}`)
  return value
}
